import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import {
  HelpCircle,
  Shield,
  Server,
  FileText,
  MessageSquare,
  Search,
  ChevronDown,
  Lock,
  Database,
  Zap,
  CheckCircle,
  FileCheck,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type ProductType = 'firmosemplice' | 'sempliceai' | 'sisemplice';
type CategoryType = 'privacy' | 'security' | 'technical' | 'support';

interface FAQItem {
  id: string;
  category: CategoryType;
  question: string;
  answer: React.ReactNode;
}

const getCategoryConfig = (t: any) => ({
  privacy: {
    label: t('faq.categories.privacy'),
    icon: Shield,
    color: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    hoverColor: 'hover:border-emerald-300 hover:shadow-emerald-100',
  },
  security: {
    label: t('faq.categories.security'),
    icon: Lock,
    color: 'bg-amber-100 text-amber-600 border-amber-200',
    hoverColor: 'hover:border-amber-300 hover:shadow-amber-100',
  },
  technical: {
    label: t('faq.categories.technical'),
    icon: Server,
    color: 'bg-blue-100 text-blue-600 border-blue-200',
    hoverColor: 'hover:border-blue-300 hover:shadow-blue-100',
  },
  support: {
    label: t('faq.categories.support'),
    icon: MessageSquare,
    color: 'bg-purple-100 text-purple-600 border-purple-200',
    hoverColor: 'hover:border-purple-300 hover:shadow-purple-100',
  },
});

function FAQCard({ item, onClick, t }: { item: FAQItem; onClick: () => void; t: any }) {
  const categoryConfig = getCategoryConfig(t);
  const config = categoryConfig[item.category];
  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        variant="default"
        onClick={onClick}
        className={`h-full overflow-hidden transition-all duration-300 border border-white/50 shadow-lg bg-white/70 backdrop-blur-md cursor-pointer ${config.hoverColor} hover:bg-white/90 hover:shadow-xl hover:-translate-y-1 group`}
      >
        <div className="p-5 flex flex-col h-full">
          {/* Header with category badge */}
          <div className="flex items-start justify-between mb-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
              <Icon className="w-3.5 h-3.5" />
              {config.label}
            </span>
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center transition-all duration-300 group-hover:bg-pink-400">
              <ChevronDown className="w-5 h-5 text-pink-500 group-hover:text-white -rotate-90" />
            </span>
          </div>

          {/* Question */}
          <h3 className="font-heading text-base font-semibold text-black leading-snug flex-grow">
            {item.question}
          </h3>

          {/* Clicca per saperne di più */}
          <p className="text-xs text-pink-500 mt-3 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            {t('faq.clickToRead')}
          </p>
        </div>
      </Card>
    </motion.div>
  );
}

function FAQModal({ item, isOpen, onClose, t }: { item: FAQItem | null; isOpen: boolean; onClose: () => void; t: any }) {
  if (!item) return null;

  const categoryConfig = getCategoryConfig(t);
  const config = categoryConfig[item.category];
  const Icon = config.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[85vh] z-50 px-4"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 border-b border-pink-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${config.color}`}>
                      <Icon className="w-4 h-4" />
                      {config.label}
                    </span>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-gray-400 hover:text-black hover:shadow-lg transition-all cursor-pointer"
                  >
                    ×
                  </button>
                </div>
                <h2 className="font-heading text-xl font-bold text-black mt-4 leading-snug">
                  {item.question}
                </h2>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                <div className="prose prose-sm max-w-none text-black/80 leading-relaxed">
                  {item.answer}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs text-gray-500">01Sistemi - {config.label}</span>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-pink-400 text-white rounded-xl text-sm font-medium hover:bg-pink-500 transition-colors cursor-pointer"
                >
                  {t('faq.close')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Colori prodotti come da header
const productColors: Record<ProductType, { active: string; inactive: string; icon: string; bgIcon: string }> = {
  firmosemplice: {
    active: 'from-cta-400 to-cta-500 border-cta-400 shadow-cta-200',
    inactive: 'border-gray-100 hover:border-cta-200',
    icon: 'text-cta-500',
    bgIcon: 'bg-cta-50',
  },
  sempliceai: {
    active: 'from-accent-400 to-accent-500 border-accent-400 shadow-accent-200',
    inactive: 'border-gray-100 hover:border-accent-200',
    icon: 'text-accent-500',
    bgIcon: 'bg-accent-50',
  },
  sisemplice: {
    active: 'from-accent-400 to-accent-500 border-accent-400 shadow-accent-200',
    inactive: 'border-gray-100 hover:border-accent-200',
    icon: 'text-accent-500',
    bgIcon: 'bg-accent-50',
  },
};

function ProductSwitcher({
  activeProduct,
  onChange,
  products,
}: {
  activeProduct: ProductType;
  onChange: (p: ProductType) => void;
  products: { id: ProductType; name: string; icon: React.ComponentType<{ className?: string }>; description: string }[];
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
      {products.map((product) => {
        const Icon = product.icon;
        const isActive = activeProduct === product.id;
        const colors = productColors[product.id];

        return (
          <button
            key={product.id}
            onClick={() => onChange(product.id)}
            className={`relative p-6 rounded-2xl text-left transition-all duration-300 border-2 cursor-pointer ${
              isActive
                ? `bg-gradient-to-br ${colors.active} text-white shadow-xl scale-[1.02]`
                : `bg-white text-black ${colors.inactive} hover:shadow-lg`
            }`}
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isActive ? 'bg-white/20' : colors.bgIcon
                }`}
              >
                <Icon className={`w-6 h-6 ${isActive ? 'text-white' : colors.icon}`} />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">{product.name}</h3>
                <p className={`text-sm mt-1 ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                  {product.description}
                </p>
              </div>
            </div>

          </button>
        );
      })}
    </div>
  );
}

// Function to get FAQs with translations
const getProductFAQs = (t: any): Record<ProductType, FAQItem[]> => ({
  firmosemplice: [
    {
      id: 'fs-privacy-policy',
      category: 'privacy',
      question: t('faq.firmosemplice.privacyPolicy'),
      answer: <p>{t('faq.firmosemplice.privacyPolicyAnswer')}</p>,
    },
    {
      id: 'fs-jurisdiction',
      category: 'privacy',
      question: t('faq.firmosemplice.jurisdiction'),
      answer: <p>{t('faq.firmosemplice.jurisdictionAnswer')}</p>,
    },
    {
      id: 'fs-authorities',
      category: 'privacy',
      question: t('faq.firmosemplice.authority'),
      answer: <p>{t('faq.firmosemplice.authorityAnswer')}</p>,
    },
    {
      id: 'fs-location',
      category: 'privacy',
      question: t('faq.firmosemplice.dataLocation'),
      answer: <p>{t('faq.firmosemplice.dataLocationAnswer')}</p>,
    },
    {
      id: 'fs-security-policy',
      category: 'security',
      question: t('faq.firmosemplice.securityPolicy'),
      answer: <p>{t('faq.firmosemplice.securityPolicyAnswer')}</p>,
    },
    {
      id: 'fs-confidentiality',
      category: 'security',
      question: t('faq.firmosemplice.confidentiality'),
      answer: <p>{t('faq.firmosemplice.confidentialityAnswer')}</p>,
    },
    {
      id: 'fs-tracking',
      category: 'security',
      question: t('faq.firmosemplice.tracking'),
      answer: <p>{t('faq.firmosemplice.trackingAnswer')}</p>,
    },
    {
      id: 'fs-encryption',
      category: 'security',
      question: t('faq.firmosemplice.encryption'),
      answer: <p>{t('faq.firmosemplice.encryptionAnswer')}</p>,
    },
    {
      id: 'fs-security-level',
      category: 'security',
      question: t('faq.firmosemplice.securityLevel'),
      answer: <p>{t('faq.firmosemplice.securityLevelAnswer')}</p>,
    },
    {
      id: 'fs-incident-management',
      category: 'security',
      question: t('faq.firmosemplice.incidents'),
      answer: <p>{t('faq.firmosemplice.incidentsAnswer')}</p>,
    },
    {
      id: 'fs-backup',
      category: 'technical',
      question: t('faq.firmosemplice.backup'),
      answer: <p>{t('faq.firmosemplice.backupAnswer')}</p>,
    },
    {
      id: 'fs-change-management',
      category: 'technical',
      question: t('faq.firmosemplice.changes'),
      answer: <p>{t('faq.firmosemplice.changesAnswer')}</p>,
    },
    {
      id: 'fs-asset-deletion',
      category: 'technical',
      question: t('faq.firmosemplice.deletion'),
      answer: <p>{t('faq.firmosemplice.deletionAnswer')}</p>,
    },
    {
      id: 'fs-support',
      category: 'support',
      question: t('faq.firmosemplice.supportContacts'),
      answer: <p>{t('faq.firmosemplice.supportContactsAnswer')}</p>,
    },
    {
      id: 'fs-signature',
      category: 'technical',
      question: t('faq.firmosemplice.signature'),
      answer: (
        <div className="space-y-2">
          <p className="text-xs">{t('faq.firmosemplice.signatureAnswer')}:</p>
          <div className="space-y-1">
            {(['Abilitazione responsabile', 'Licenza e tavoletta', 'Documento adesione'] as string[]).map((req: string) => (
              <div key={req} className="flex items-center gap-2 text-xs">
                <CheckCircle className="w-3 h-3 text-emerald-500" />
                <span>{req}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'fs-decommissioning',
      category: 'technical',
      question: t('faq.firmosemplice.decommissioning'),
      answer: <p>{t('faq.firmosemplice.decommissioningAnswer')}</p>,
    },
  ],
  sempliceai: [
    {
      id: 'sai-privacy-policy',
      category: 'privacy',
      question: t('faq.sempliceai.privacyPolicy'),
      answer: <p>{t('faq.sempliceai.privacyPolicyAnswer')}</p>,
    },
    {
      id: 'sai-jurisdiction',
      category: 'privacy',
      question: t('faq.sempliceai.laws'),
      answer: <p>{t('faq.sempliceai.lawsAnswer')}</p>,
    },
    {
      id: 'sai-location',
      category: 'privacy',
      question: t('faq.sempliceai.hosting'),
      answer: <p>{t('faq.sempliceai.hostingAnswer')}</p>,
    },
    {
      id: 'sai-security-policy',
      category: 'security',
      question: t('faq.sempliceai.security'),
      answer: <p>{t('faq.sempliceai.securityAnswer')}</p>,
    },
    {
      id: 'sai-encryption',
      category: 'security',
      question: t('faq.sempliceai.encryption'),
      answer: <p>{t('faq.sempliceai.encryptionAnswer')}</p>,
    },
    {
      id: 'sai-security-level',
      category: 'security',
      question: t('faq.firmosemplice.securityLevel'),
      answer: <p>{t('faq.sempliceai.securityAnswer')}</p>,
    },
    {
      id: 'sai-asset-deletion',
      category: 'technical',
      question: t('faq.firmosemplice.deletion'),
      answer: <p>{t('faq.sempliceai.retentionAnswer')}</p>,
    },
    {
      id: 'sai-support',
      category: 'support',
      question: t('faq.firmosemplice.supportContacts'),
      answer: <p>{t('faq.sempliceai.rightsAnswer')}</p>,
    },
  ],
  sisemplice: [
    {
      id: 'ss-privacy-policy',
      category: 'privacy',
      question: t('faq.sisemplice.privacyPolicy'),
      answer: <p>{t('faq.sisemplice.privacyPolicyAnswer')}</p>,
    },
    {
      id: 'ss-jurisdiction',
      category: 'privacy',
      question: t('faq.sisemplice.authority'),
      answer: <p>{t('faq.sisemplice.authorityAnswer')}</p>,
    },
    {
      id: 'ss-security-policy',
      category: 'security',
      question: t('faq.sisemplice.security'),
      answer: <p>{t('faq.sisemplice.securityAnswer')}</p>,
    },
    {
      id: 'ss-tracking',
      category: 'security',
      question: t('faq.firmosemplice.tracking'),
      answer: <p>{t('faq.sisemplice.accessAnswer')}</p>,
    },
    {
      id: 'ss-security-level',
      category: 'security',
      question: t('faq.firmosemplice.securityLevel'),
      answer: <p>{t('faq.sisemplice.certificationsAnswer')}</p>,
    },
    {
      id: 'ss-visibility-levels',
      category: 'technical',
      question: t('faq.sisemplice.laws'),
      answer: <p>{t('faq.sisemplice.lawsAnswer')}</p>,
    },
    {
      id: 'ss-massive-processing',
      category: 'technical',
      question: t('faq.sisemplice.hosting'),
      answer: <p>{t('faq.sisemplice.hostingAnswer')}</p>,
    },
    {
      id: 'ss-signature',
      category: 'technical',
      question: t('faq.firmosemplice.signature'),
      answer: <p>{t('faq.sisemplice.backupAnswer')}</p>,
    },
    {
      id: 'ss-decommissioning',
      category: 'technical',
      question: t('faq.firmosemplice.decommissioning'),
      answer: <p>{t('faq.sisemplice.supportAnswer')}</p>,
    },
    {
      id: 'ss-support',
      category: 'support',
      question: t('faq.firmosemplice.supportContacts'),
      answer: <p>{t('faq.sisemplice.supportAnswer')}</p>,
    },
  ],
});

export function FAQ() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [activeProduct, setActiveProduct] = useState<ProductType>('firmosemplice');
  const [activeCategory, setActiveCategory] = useState<CategoryType | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all' as const, label: t('faq.categories.all'), icon: FileText },
    { id: 'privacy' as CategoryType, label: t('faq.categories.privacy'), icon: Shield },
    { id: 'security' as CategoryType, label: t('faq.categories.security'), icon: Lock },
    { id: 'technical' as CategoryType, label: t('faq.categories.technical'), icon: Server },
    { id: 'support' as CategoryType, label: t('faq.categories.support'), icon: MessageSquare },
  ];

  const products = [
    {
      id: 'firmosemplice' as ProductType,
      name: 'FirmoSemplice',
      description: t('home.products.firmo.description'),
      icon: FileCheck,
    },
    {
      id: 'sempliceai' as ProductType,
      name: 'SempliceAI',
      description: t('home.products.ai.description'),
      icon: Zap,
    },
    {
      id: 'sisemplice' as ProductType,
      name: 'SiSemplice',
      description: t('home.businessUnits.sisemplice.description'),
      icon: Database,
    },
  ];

  const productFAQs = useMemo(() => getProductFAQs(t), [t]);
  const currentProduct = products.find((p) => p.id === activeProduct)!;
  const currentFAQs = productFAQs[activeProduct];

  const filteredFAQs = useMemo(() => {
    let faqs = currentFAQs;
    if (activeCategory !== 'all') {
      faqs = faqs.filter((item) => item.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      faqs = faqs.filter(
        (item) =>
          item.question.toLowerCase().includes(query) ||
          (typeof item.answer === 'string' && item.answer.toLowerCase().includes(query))
      );
    }
    return faqs;
  }, [currentFAQs, activeCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: currentFAQs.length };
    const categoryConfig = getCategoryConfig(t);
    (Object.keys(categoryConfig) as CategoryType[]).forEach((cat) => {
      counts[cat] = currentFAQs.filter((f) => f.category === cat).length;
    });
    return counts;
  }, [currentFAQs, t]);

  return (
    <>
      <Helmet>
        <title>{t('faq.pageTitle', { product: currentProduct.name })}</title>
        <meta
          name="description"
          content={t('faq.metaDescription', { product: currentProduct.name })}
        />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/faq`} />
      </Helmet>

      {/* Hero Section - Pink Glass Effect */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Pink glass background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/80 via-rose-50/90 to-pink-50/80" />
        <div className="absolute top-0 left-0 w-full h-full bg-white/30 backdrop-blur-sm" />

        {/* Decorative glass blobs */}
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-pink-300/40 rounded-full blur-[100px]" />
        <div className="absolute top-20 right-[15%] w-96 h-96 bg-rose-300/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-10 left-[30%] w-80 h-80 bg-fuchsia-200/30 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 right-[5%] w-64 h-64 bg-pink-400/20 rounded-full blur-[80px]" />

        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-400/10 text-pink-600 text-sm font-medium backdrop-blur-md border border-pink-200/50">
                <HelpCircle className="w-4 h-4" />
                {t('faq.overline')}
              </span>
              <h1 className="heading-1 mt-6 text-black">{t('faq.heroTitle')}</h1>
              <p className="body-large mt-4 text-black/80">
                {t('faq.heroDescription')}
              </p>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-8 relative max-w-2xl mx-auto"
            >
              <div className="relative flex items-center">
                {/* Logo/Icona di ricerca prominente */}
                <div className="absolute left-3 z-10 w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg shadow-pink-200">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <input
                  type="text"
                  placeholder={t('faq.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-16 pr-12 py-4 rounded-2xl bg-white/80 backdrop-blur-md shadow-xl text-black placeholder:text-black/70 focus:outline-none focus:ring-4 focus:ring-pink-300/50 border border-white/50 text-lg"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 hover:bg-pink-200 transition-colors"
                  >
                    ×
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Product Switcher Section */}
      <Section className="bg-gray-50 -mt-8 relative z-10" padding="md">
        <Container>
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
            <ProductSwitcher
              activeProduct={activeProduct}
              onChange={(p) => {
                setActiveProduct(p);
                setActiveCategory('all');
                setOpenId(null);
              }}
              products={products}
            />

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const count = categoryCounts[cat.id] || 0;
                const isActive = activeCategory === cat.id;

                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id as CategoryType | 'all')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all cursor-pointer ${
                      isActive
                        ? 'bg-primary-400 text-white shadow-lg shadow-primary-200'
                        : 'bg-gray-100 text-black/80 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{cat.label}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive ? 'bg-white/20' : 'bg-white'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </Container>
      </Section>

      {/* FAQ Grid Section */}
      <Section padding="lg" className="bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 relative overflow-hidden">
        {/* Glassmorphism decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-rose-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-200/10 rounded-full blur-3xl" />

        <Container className="relative z-10">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="heading-3 text-black/70">{t('faq.noResults')}</h3>
              <p className="text-black mt-2">{t('faq.noResultsDescription')}</p>
            </div>
          ) : (
            <>
              <motion.div
                layout
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
              >
                <AnimatePresence mode="popLayout">
                  {filteredFAQs.map((item) => (
                    <FAQCard
                      key={item.id}
                      item={item}
                      onClick={() => setOpenId(item.id)}
                      t={t}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* FAQ Modal */}
              <FAQModal
                item={filteredFAQs.find((f) => f.id === openId) || null}
                isOpen={!!openId}
                onClose={() => setOpenId(null)}
                t={t}
              />
            </>
          )}
        </Container>
      </Section>
    </>
  );
}
